import { Link } from "react-router-dom";
import { Heart, Handshake, Compass, ShieldCheck, ArrowRight, Phone } from "lucide-react";
import { BUSINESS } from "../lib/api";
import { SEO } from "../components/SEO";

const values = [
  { icon: Heart, title: "People before product", desc: "We take the time to understand your life first — the loan comes second." },
  { icon: Handshake, title: "Straight-talking advice", desc: "No jargon, no small print gotchas. Just clear, honest recommendations." },
  { icon: Compass, title: "Options over pressure", desc: "You’ll always see multiple lenders side-by-side. You choose." },
  { icon: ShieldCheck, title: "There for the long run", desc: "We review your loan every year to make sure it still fits." },
];

export default function About() {
  return (
    <div>
      <SEO
        path="/about"
        title="About Us — Australia-wide Finance Broker"
        description="Sixty Finance is an Australia-wide finance and mortgage broker offering honest advice, tailored lending solutions and long-term client care. Headquartered in Craigieburn, Victoria."
      />
      <section className="relative bg-emerald-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80"
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">About Sixty Finance</p>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl">
            Local, honest, <span className="text-emerald-300">always by your side.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-emerald-100/85 text-base md:text-lg leading-relaxed">
            Sixty Finance was founded on a simple idea: finance advice should
            feel like a conversation with a mate who happens to know the
            numbers cold. We proudly serve clients all over Australia —
            families, first home buyers, investors and business owners.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-14 items-start">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-emerald-950">
            Big-broker access, boutique-broker care.
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed">
            We built Sixty Finance because too many borrowers were being
            treated like a case number. We wanted to run a broker firm where
            every client — whether borrowing $20k or $2m — gets the same time,
            focus, and follow-through.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Today we have accreditations with 40+ lenders across residential,
            commercial and asset finance. We keep our team small on purpose,
            so clients get direct lines to their broker — not a call centre
            queue.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/team"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800 transition-colors"
              data-testid="about-team-btn"
            >
              Meet the team <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="about-call-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-emerald-200 text-emerald-900 font-semibold text-sm hover:bg-emerald-50 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="num">{BUSINESS.phone}</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {[
            { n: "40+", l: "Lenders on panel" },
            { n: "$180M+", l: "Loans arranged" },
            { n: "600+", l: "Happy borrowers" },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-2xl bg-emerald-50/50 border border-emerald-100 p-6"
            >
              <div className="text-3xl md:text-4xl font-bold text-emerald-900 num">
                {s.n}
              </div>
              <div className="mt-2 text-sm text-emerald-800/80">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Our values</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-emerald-950 max-w-2xl">
            What guides every recommendation we make.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="rounded-2xl border border-emerald-100 p-6 bg-emerald-50/30 hover:-translate-y-1 hover:shadow-lg transition-transform duration-300"
                >
                  <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-900 text-emerald-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-semibold text-emerald-950">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
